// Mercapoli - Admin Solicitudes
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Store, Bike, Loader2, CheckCircle, XCircle,
  Clock, Mail, Phone, MapPin, FileText, User, Eye, Plus
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import apiClient from '../../lib/api';
import { toast } from 'sonner';

const AdminSolicitudes = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [solicitudesComercios, setSolicitudesComercios] = useState([]);
  const [solicitudesRiders, setSolicitudesRiders] = useState([]);
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(null); // 'aprobar' o 'rechazar'
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [comercios, riders] = await Promise.all([
        apiClient.get('/admin/solicitudes/comercios'),
        apiClient.get('/admin/solicitudes/riders')
      ]);
      setSolicitudesComercios(comercios.data);
      setSolicitudesRiders(riders.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar solicitudes');
    }
    setLoading(false);
  };

  const openDetail = (solicitud, tipo) => {
    setSelectedSolicitud({ ...solicitud, tipo });
    setShowDetailModal(true);
  };

  const openActionModal = (tipo) => {
    setActionType(tipo);
    setMotivoRechazo('');
    setComisionPorcentaje(25);  // Default Freemium
    setPlanSeleccionado('freemium');
    setShowDetailModal(false);
    setShowActionModal(true);
  };

  const [comisionPorcentaje, setComisionPorcentaje] = useState(25);
  const [planSeleccionado, setPlanSeleccionado] = useState('freemium');

  const procesarSolicitud = async () => {
    if (!selectedSolicitud) return;
    
    setProcessing(true);
    try {
      let endpoint;
      let params = {};
      
      if (selectedSolicitud.tipo === 'comercio') {
        if (actionType === 'aprobar') {
          endpoint = `/admin/solicitudes/comercios/${selectedSolicitud.solicitud_id}/aprobar`;
          params = { comision_porcentaje: comisionPorcentaje, plan: planSeleccionado };
        } else {
          endpoint = `/admin/solicitudes/comercios/${selectedSolicitud.solicitud_id}/rechazar`;
        }
      } else {
        endpoint = `/admin/solicitudes/riders/${selectedSolicitud.solicitud_id}/${actionType}`;
      }
      
      await apiClient.put(endpoint, { 
        motivo: actionType === 'rechazar' ? motivoRechazo : null 
      }, { params });
      
      toast.success(`Solicitud ${actionType === 'aprobar' ? 'aprobada' : 'rechazada'}`);
      setShowActionModal(false);
      setSelectedSolicitud(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar solicitud');
    }
    setProcessing(false);
  };

  const getEstadoBadge = (estado) => {
    const styles = {
      pendiente: 'bg-yellow-100 text-yellow-800',
      aprobada: 'bg-green-100 text-green-800',
      rechazada: 'bg-red-100 text-red-800'
    };
    return styles[estado] || styles.pendiente;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-GT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const pendientesComercios = solicitudesComercios.filter(s => s.estado === 'pendiente').length;
  const pendientesRiders = solicitudesRiders.filter(s => s.estado === 'pendiente').length;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/admin')} className="p-2 -ml-2 rounded-lg hover:bg-muted">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="font-heading text-lg font-bold">Solicitudes</h1>
            </div>
            <div className="flex items-center gap-2">
              {(pendientesComercios + pendientesRiders) > 0 && (
                <Badge className="bg-orange-100 text-orange-800">
                  {pendientesComercios + pendientesRiders} pendientes
                </Badge>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="container-app py-6">
        <Tabs defaultValue="comercios" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="comercios" className="flex items-center gap-2">
              <Store className="h-4 w-4" />
              Comercios ({solicitudesComercios.length})
              {pendientesComercios > 0 && (
                <Badge className="bg-yellow-500 text-white text-xs ml-1">{pendientesComercios}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="riders" className="flex items-center gap-2">
              <Bike className="h-4 w-4" />
              Riders ({solicitudesRiders.length})
              {pendientesRiders > 0 && (
                <Badge className="bg-yellow-500 text-white text-xs ml-1">{pendientesRiders}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Tab Comercios */}
          <TabsContent value="comercios">
            <div className="flex justify-end mb-4">
              <Button 
                onClick={() => navigate('/admin/restaurantes/nuevo')}
                className="bg-brand-primary hover:bg-brand-secondary"
              >
                <Plus className="h-4 w-4 mr-2" />
                Agregar Comercio Manual
              </Button>
            </div>
            
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
              </div>
            ) : solicitudesComercios.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Store className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No hay solicitudes de comercios</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {solicitudesComercios.map((sol) => (
                  <Card key={sol.solicitud_id} className={sol.estado === 'pendiente' ? 'border-l-4 border-l-yellow-500' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-brand-primary/10 flex items-center justify-center">
                            <Store className="h-6 w-6 text-brand-primary" />
                          </div>
                          <div>
                            <h3 className="font-medium">{sol.nombre_comercio}</h3>
                            <p className="text-sm text-muted-foreground">{sol.nombre_propietario}</p>
                            <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {sol.ciudad}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatDate(sol.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Badge className={getEstadoBadge(sol.estado)}>
                            {sol.estado}
                          </Badge>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openDetail(sol, 'comercio')}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Ver
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Tab Riders */}
          <TabsContent value="riders">
            <div className="flex justify-end mb-4">
              <Button 
                onClick={() => navigate('/admin/repartidores/nuevo')}
                className="bg-brand-accent hover:bg-brand-accent/90 text-black"
              >
                <Plus className="h-4 w-4 mr-2" />
                Agregar Rider Manual
              </Button>
            </div>
            
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
              </div>
            ) : solicitudesRiders.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Bike className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No hay solicitudes de riders</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {solicitudesRiders.map((sol) => (
                  <Card key={sol.solicitud_id} className={sol.estado === 'pendiente' ? 'border-l-4 border-l-yellow-500' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-brand-accent/20 flex items-center justify-center">
                            <Bike className="h-6 w-6 text-brand-accent" />
                          </div>
                          <div>
                            <h3 className="font-medium">{sol.nombre}</h3>
                            <p className="text-sm text-muted-foreground">{sol.email}</p>
                            <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {sol.ciudad}
                              </span>
                              <span className="capitalize">{sol.vehiculo}</span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatDate(sol.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Badge className={getEstadoBadge(sol.estado)}>
                            {sol.estado}
                          </Badge>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openDetail(sol, 'rider')}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Ver
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal de Detalle */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedSolicitud?.tipo === 'comercio' ? (
                <Store className="h-5 w-5 text-brand-primary" />
              ) : (
                <Bike className="h-5 w-5 text-brand-accent" />
              )}
              {selectedSolicitud?.tipo === 'comercio' ? 'Solicitud de Comercio' : 'Solicitud de Rider'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedSolicitud && (
            <div className="space-y-4 py-4">
              {selectedSolicitud.tipo === 'comercio' ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Comercio</p>
                      <p className="font-medium">{selectedSolicitud.nombre_comercio}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Tipo</p>
                      <p className="font-medium">{selectedSolicitud.tipo_comercio_id}</p>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-xs text-muted-foreground">Dirección</p>
                    <p className="font-medium">{selectedSolicitud.direccion}, {selectedSolicitud.ciudad}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Teléfono Comercio</p>
                      <p className="font-medium">{selectedSolicitud.telefono_comercio}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Email Comercio</p>
                      <p className="font-medium">{selectedSolicitud.email_comercio || 'N/A'}</p>
                    </div>
                  </div>
                  
                  <hr />
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Propietario</p>
                      <p className="font-medium">{selectedSolicitud.nombre_propietario}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">DPI</p>
                      <p className="font-medium">{selectedSolicitud.documento_identidad}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Email Propietario</p>
                      <p className="font-medium">{selectedSolicitud.email_propietario}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Teléfono Propietario</p>
                      <p className="font-medium">{selectedSolicitud.telefono_propietario}</p>
                    </div>
                  </div>
                  
                  {selectedSolicitud.nit_rfc && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">NIT</p>
                        <p className="font-medium">{selectedSolicitud.nit_rfc}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Razón Social</p>
                        <p className="font-medium">{selectedSolicitud.razon_social || 'N/A'}</p>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Nombre</p>
                      <p className="font-medium">{selectedSolicitud.nombre}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">DPI</p>
                      <p className="font-medium">{selectedSolicitud.documento_identidad}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Email</p>
                      <p className="font-medium">{selectedSolicitud.email}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Teléfono</p>
                      <p className="font-medium">{selectedSolicitud.telefono}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Ciudad</p>
                      <p className="font-medium">{selectedSolicitud.ciudad}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Vehículo</p>
                      <p className="font-medium capitalize">{selectedSolicitud.vehiculo}</p>
                    </div>
                  </div>
                  
                  {selectedSolicitud.licencia && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Licencia</p>
                        <p className="font-medium">{selectedSolicitud.licencia}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Placa</p>
                        <p className="font-medium">{selectedSolicitud.placa_vehiculo || 'N/A'}</p>
                      </div>
                    </div>
                  )}
                  
                  {selectedSolicitud.banco && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Banco</p>
                        <p className="font-medium">{selectedSolicitud.banco}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Cuenta</p>
                        <p className="font-medium">{selectedSolicitud.cuenta_banco}</p>
                      </div>
                    </div>
                  )}
                </>
              )}
              
              <div className="pt-2">
                <p className="text-xs text-muted-foreground">Fecha de solicitud</p>
                <p className="font-medium">{formatDate(selectedSolicitud.created_at)}</p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            {selectedSolicitud?.estado === 'pendiente' && (
              <>
                <Button 
                  variant="outline" 
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => openActionModal('rechazar')}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Rechazar
                </Button>
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => openActionModal('aprobar')}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Aprobar
                </Button>
              </>
            )}
            {selectedSolicitud?.estado !== 'pendiente' && (
              <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                Cerrar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Acción */}
      <Dialog open={showActionModal} onOpenChange={setShowActionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'aprobar' ? '✅ Aprobar Solicitud' : '❌ Rechazar Solicitud'}
            </DialogTitle>
            <DialogDescription>
              {actionType === 'aprobar' 
                ? 'Se creará la cuenta y se notificará al solicitante.'
                : 'El solicitante será notificado del rechazo.'}
            </DialogDescription>
          </DialogHeader>
          
          {actionType === 'rechazar' && (
            <div className="space-y-2 py-4">
              <Label>Motivo del rechazo (opcional)</Label>
              <Textarea
                value={motivoRechazo}
                onChange={(e) => setMotivoRechazo(e.target.value)}
                placeholder="Ej: Documentación incompleta, zona no disponible..."
                rows={3}
              />
            </div>
          )}
          
          {actionType === 'aprobar' && (
            <div className="py-4 space-y-4">
              {selectedSolicitud?.tipo === 'comercio' && (
                <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                  <div className="space-y-2">
                    <Label className="text-blue-800 dark:text-blue-200">Plan</Label>
                    <select
                      value={planSeleccionado}
                      onChange={(e) => {
                        setPlanSeleccionado(e.target.value);
                        // Auto-ajustar comisión según plan
                        const comisiones = { freemium: 25, basico: 15, pro: 10 };
                        setComisionPorcentaje(comisiones[e.target.value] || 15);
                      }}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="freemium">Plan Freemium - $0 USD/mes (comisión 25%)</option>
                      <option value="basico">Plan Básico - $28 USD/mes (comisión 15%)</option>
                      <option value="pro">Plan Pro - $42 USD/mes (comisión 10%)</option>
                    </select>
                    <p className="text-xs text-muted-foreground">
                      {planSeleccionado === 'freemium' && '⚡ Sin costo mensual, ideal para nuevos comercios'}
                      {planSeleccionado === 'basico' && '📦 Plan estándar con comisión balanceada'}
                      {planSeleccionado === 'pro' && '🚀 Menor comisión para comercios de alto volumen'}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-blue-800 dark:text-blue-200">Comisión negociada (%)</Label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      value={comisionPorcentaje}
                      onChange={(e) => setComisionPorcentaje(parseFloat(e.target.value))}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                    <p className="text-xs text-muted-foreground">
                      Comisión que se cobrará sobre cada venta del comercio (ajustable)
                    </p>
                  </div>
                </div>
              )}
              
              <div className="p-4 bg-green-50 rounded-lg text-sm">
                <p className="font-medium text-green-800 mb-2">Se realizará lo siguiente:</p>
                <ul className="text-green-700 space-y-1">
                  {selectedSolicitud?.tipo === 'comercio' ? (
                    <>
                      <li>• Se creará el comercio con comisión del {comisionPorcentaje}%</li>
                      <li>• Se asignará el {planSeleccionado === 'freemium' ? 'Plan Freemium (sin costo mensual)' : planSeleccionado === 'pro' ? 'Plan Pro' : 'Plan Básico'}</li>
                      <li>• Se creará una cuenta de usuario para el propietario</li>
                      <li>• El comercio deberá aceptar la comisión para operar</li>
                    </>
                  ) : (
                    <>
                      <li>• Se creará la cuenta de Rider</li>
                      <li>• Se habilitará para recibir pedidos</li>
                      <li>• Se enviará email con credenciales de acceso</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={procesarSolicitud}
              disabled={processing}
              className={actionType === 'aprobar' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {actionType === 'aprobar' ? 'Confirmar Aprobación' : 'Confirmar Rechazo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSolicitudes;
